<script lang="ts">
  export type WidgetSkeletonVariant =
    | 'stat'
    | 'donuts'
    | 'gauge'
    | 'stage-bars'
    | 'vertical-bars'
    | 'bar-list';
</script>

<script setup lang="ts">
  import { Skeleton } from '@/shared/ui';

  interface Props {
    variant?: WidgetSkeletonVariant;
    rows?: number;
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'bar-list',
    rows: 5,
  });

  const BAR_LIST_WIDTHS = ['w-full', 'w-4/5', 'w-3/5', 'w-2/5', 'w-1/5', 'w-3/4', 'w-1/2'];
  const VERTICAL_BAR_HEIGHTS = ['h-3/5', 'h-full', 'h-4/5', 'h-1/4', 'h-2/3'];
</script>

<template>
  <div
    data-testid="widget-skeleton"
    class="flex h-full flex-col"
  >
    <template v-if="props.variant === 'stat'">
      <div class="flex h-full flex-col justify-end gap-5">
        <Skeleton class="h-9 w-36" />
        <div class="flex items-center gap-2">
          <Skeleton class="h-6 w-24 rounded-full" />
          <Skeleton class="h-4 w-28" />
        </div>
      </div>
    </template>

    <template v-else-if="props.variant === 'donuts'">
      <div class="flex h-full gap-6">
        <div
          v-for="half in 2"
          :key="half"
          class="flex flex-1 flex-col items-center gap-4"
        >
          <Skeleton class="h-4 w-28 self-start" />
          <div class="relative flex flex-1 items-center justify-center">
            <Skeleton class="size-40 rounded-full" />
            <span class="absolute size-24 rounded-full bg-white-01" />
          </div>
          <div class="flex items-center gap-4">
            <Skeleton class="h-3 w-18" />
            <Skeleton class="h-3 w-18" />
          </div>
          <div class="flex items-center gap-2 self-start">
            <Skeleton class="h-6 w-32 rounded-full" />
            <Skeleton class="h-4 w-24" />
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="props.variant === 'gauge'">
      <div class="flex h-full flex-col items-center justify-between gap-4">
        <div class="relative h-26 w-52 overflow-hidden">
          <Skeleton class="absolute inset-x-0 top-0 size-52 rounded-full" />
          <span class="absolute left-1/2 top-13 size-26 -translate-x-1/2 rounded-full bg-white-01" />
        </div>
        <div class="flex items-center gap-4">
          <Skeleton class="h-3 w-18" />
          <Skeleton class="h-3 w-18" />
        </div>
        <div class="flex items-center gap-2 self-start">
          <Skeleton class="h-6 w-40 rounded-full" />
          <Skeleton class="h-4 w-24" />
        </div>
      </div>
    </template>

    <template v-else-if="props.variant === 'stage-bars'">
      <div class="flex h-full flex-col justify-between gap-6 py-2">
        <div
          v-for="group in 2"
          :key="group"
          class="flex flex-col gap-4"
        >
          <Skeleton class="h-4 w-24" />
          <Skeleton class="h-6 w-3/5" />
          <Skeleton class="h-6 w-4/5" />
          <div class="flex items-center gap-2">
            <Skeleton class="h-6 w-36 rounded-full" />
            <Skeleton class="h-4 w-24" />
          </div>
          <Skeleton class="h-4 w-52" />
        </div>
      </div>
    </template>

    <template v-else-if="props.variant === 'vertical-bars'">
      <div class="flex h-full flex-col gap-4">
        <div class="flex min-h-0 flex-1 items-end justify-between gap-3 px-2">
          <Skeleton
            v-for="(height, index) in VERTICAL_BAR_HEIGHTS"
            :key="index"
            class="w-10 rounded-t-md"
            :class="height"
          />
        </div>
        <div class="flex items-center gap-2">
          <Skeleton class="h-6 w-36 rounded-full" />
          <Skeleton class="h-4 w-24" />
        </div>
      </div>
    </template>

    <template v-else>
      <div class="flex h-full flex-col justify-center gap-4">
        <div
          v-for="index in props.rows"
          :key="index"
          class="flex items-center gap-3"
        >
          <Skeleton class="h-4 w-24 shrink-0" />
          <Skeleton
            class="h-5"
            :class="BAR_LIST_WIDTHS[(index - 1) % BAR_LIST_WIDTHS.length]"
          />
        </div>
      </div>
    </template>
  </div>
</template>
